import { Box, Typography, Grid2 as Grid, Paper, Tooltip, IconButton, InputBase } from "@mui/material";
import { Add, ContentCopy } from "@mui/icons-material";
import { Button, useNotify } from "react-admin";
import { useState } from "react";
import { BreadcrumbNav } from "../BreadCrumbNav";
import { GhostButton } from "@/components/common/GhostButton";
import { CreateInDialog } from "@/components/common/CreateInDialog";
import { AlertType, ResourceName } from "@/types";
import { useAppTheme } from "@/hooks/useAppTheme";

const CategorySection = ({
  title,
  components,
  search,
}: {
  title: string;
  components: {
    name: string;
    description: string;
    code: string;
    preview: React.ReactNode;
  }[];
  search: string;
}) => {
  const { isDarkMode } = useAppTheme();
  const notify = useNotify();

  const handleCopy = async (code: string) => {
    await navigator.clipboard.writeText(code);
    notify("Copied to clipboard!", { type: AlertType.SUCCESS });
  };

  const filteredComponents = components.filter(
    ({ name, description }) =>
      name.toLowerCase().includes(search.toLowerCase()) || description.toLowerCase().includes(search.toLowerCase())
  );

  if (filteredComponents.length === 0) return null;

  return (
    <Box mb={6}>
      <Typography variant="h2" gutterBottom>
        {title}
      </Typography>
      <Grid container spacing={4}>
        {filteredComponents.map(({ name, description, code, preview }) => (
          <Grid key={name} size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h4">{name}</Typography>
                <Tooltip title="Copy usage">
                  <IconButton onClick={() => handleCopy(code)}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
              <Box>{preview}</Box>
              <Paper
                sx={{
                  backgroundColor: isDarkMode ? "#1e1e1e" : "#f5f5f5",
                  p: 2,
                  fontFamily: "monospace",
                  fontSize: 14,
                  borderRadius: 1,
                  overflowX: "auto",
                  whiteSpace: "pre-wrap",
                }}
              >
                <code>{code}</code>
              </Paper>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export const ComponentsGuide = () => {
  const [search, setSearch] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  return (
    <Box sx={{ p: 4 }}>
      <BreadcrumbNav current="Components" />
      <Box mb={4}>
        <Typography variant="h1" gutterBottom>
          Custom Components Guide
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          A reference of reusable UI components across the product.
        </Typography>
        <InputBase
          placeholder="Search components"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            px: 2,
            py: 1,
            width: 300,
          }}
        />
      </Box>

      <CategorySection
        title="Buttons"
        search={search}
        components={[
          {
            name: "GhostButton",
            description: "Button used for non-primary, non-destructive actions or alternatives in modals",
            code: '<GhostButton label="Ghost Button" onClick={handleClick} />',
            preview: <GhostButton label="Ghost Button" onClick={() => {}} />,
          },
        ]}
      />

      <CategorySection
        title="Display Components"
        search={search}
        components={[
          {
            name: "CreateInDialog",
            description:
              "A reusable dialog-based creation component for resources like policies or claims. Useful when inline creation is preferred over page navigation.",
            code: `
import { CreateInDialog } from "./CreateInDialog";
import { Add } from "@mui/icons-material";

const [open, setOpen] = useState(false);

<Button
  startIcon={<Add />}
  label="Create"
  variant="contained"
  onClick={() => setCreateDialogOpen(true)}
/>
<CreateInDialog open={open} onClose={() => setOpen(false)} resource="replace me" />
  `.trim(),
            preview: (
              <>
                <Button
                  startIcon={<Add />}
                  label="Create"
                  variant="contained"
                  onClick={() => setCreateDialogOpen(true)}
                />
                <CreateInDialog
                  open={createDialogOpen}
                  onClose={() => setCreateDialogOpen(false)}
                  resource={ResourceName.CLIENTS}
                />
              </>
            ),
          },
        ]}
      />
    </Box>
  );
};
